import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventIcon from '@mui/icons-material/Event';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import { Container, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import GradientHeading from 'components/GradientHeading';
import { useEffect, useState } from 'react';
import { getWorkshops } from 'services/workshop';
import resolveThemeColor from 'utils/resolveThemeColor';

const WorkshopSchedule = () => {
    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up('md'), {
        defaultMatches: true,
    });
    const [selectedDay, setSelectedDay] = useState(0);
    const [workshops, setWorkshops] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const handleTabChange = (_, newValue) => {
        setSelectedDay(newValue);
    };

    useEffect(() => {
        let mounted = true;
        const fetchWorkshops = async () => {
            try {
                const data = await getWorkshops();
                if (mounted) {
                    setWorkshops(data || []);
                    setSelectedDay(0);
                }
            } catch (e) {
                if (mounted) setError(e);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        fetchWorkshops();
        return () => {
            mounted = false;
        };
    }, []);

    if (loading) {
        return (
            <Container width={isMd ? '85vw' : '100vw'} my={2}>
                <Box py={6}>
                    <GradientHeading label="Workshop Schedule" />
                    <Typography color="text.secondary">
                        Loading schedule…
                    </Typography>
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container width={isMd ? '85vw' : '100vw'} my={2}>
                <Box py={6}>
                    <GradientHeading label="Workshop Schedule" />
                    <Typography color="error">
                        Failed to load workshops.
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Box
            sx={{
                py: { xs: 8, md: 12 },
                background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, white 60%, ${theme.palette.primary.main}08 100%)`,
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Decorative background */}
            <Box
                sx={{
                    position: 'absolute',
                    top: -60,
                    right: -60,
                    width: 220,
                    height: 220,
                    borderRadius: '50%',
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
                    zIndex: 0,
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: -120,
                    left: -120,
                    width: 320,
                    height: 320,
                    borderRadius: '50%',
                    background: `linear-gradient(45deg, ${theme.palette.secondary.main}10, ${theme.palette.primary.main}10)`,
                    zIndex: 0,
                }}
            />
            <Container width={isMd ? '85vw' : '100vw'} my={2}>
                <Box py={6}>
                    <Box marginBottom={4}>
                        <GradientHeading label="Workshop Schedule" />

                        {/* Day Tabs */}
                        <Box
                            sx={{
                                borderBottom: 1,
                                borderColor: 'divider',
                                mb: 4,
                            }}
                        >
                            <Tabs
                                value={selectedDay}
                                onChange={handleTabChange}
                                centered
                                sx={{
                                    '& .MuiTab-root': {
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        fontSize: '1.1rem',
                                        minWidth: 200,
                                        py: 0,
                                    },
                                }}
                            >
                                {workshops?.map((day, idx) => (
                                    <Tab
                                        sx={{
                                            px: 0,
                                        }}
                                        key={day.day}
                                        label={
                                            <Box
                                                textAlign="center"
                                                sx={{
                                                    backgroundColor:
                                                        selectedDay === idx
                                                            ? theme.palette.primary.main
                                                            : 'transparent',
                                                    color:
                                                        selectedDay === idx
                                                            ? theme.palette.primary.contrastText
                                                            : 'inherit',
                                                    borderRadius: 1,
                                                    px: 3,
                                                    py: 1,
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    color="inherit"
                                                >
                                                    {day.date}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                ))}
                            </Tabs>
                        </Box>
                    </Box>

                    {/* Schedule Content */}
                    <Box>
                        <Box display="flex" alignItems="center" mb={3}>
                            <EventIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography
                                variant="h5"
                                fontWeight={700}
                                color="primary"
                                gutterBottom
                                sx={{ mb: 0 }}
                            >
                                {workshops && workshops[selectedDay]?.date}
                            </Typography>
                        </Box>

                        <Grid container spacing={3}>
                            {workshops &&
                                workshops[selectedDay]?.workshops?.map(
                                    (workshop, index) => {
                                        return (
                                            <Grid
                                                item
                                                xs={12}
                                                lg={6}
                                                key={index}
                                            >
                                                <Card
                                                    sx={{
                                                        height: '100%',
                                                        borderRadius: 3,
                                                        boxShadow:
                                                            '0 4px 20px rgba(0,0,0,0.08)',
                                                        transition:
                                                            'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                        border: `1px solid ${theme.palette.divider}`,
                                                        '&:hover': {
                                                            transform:
                                                                'translateY(-8px)',
                                                            boxShadow:
                                                                '0 12px 40px rgba(0,0,0,0.15)',
                                                        },
                                                    }}
                                                >
                                                    {/* Type Header */}
                                                    <Box
                                                        sx={{
                                                            background: `linear-gradient(135deg, ${resolveThemeColor(
                                                                workshop
                                                                    .workshopType
                                                                    .background,
                                                                theme,
                                                            )} 0%, ${resolveThemeColor(
                                                                workshop
                                                                    .workshopType
                                                                    .backgroundSecondary,
                                                                theme,
                                                            )} 100%)`,
                                                            color: resolveThemeColor(
                                                                theme.palette
                                                                    .primary
                                                                    .contrastText,
                                                                theme,
                                                            ),
                                                            p: 2.5,
                                                            borderRadius:
                                                                '12px 12px 0 0',
                                                        }}
                                                    >
                                                        <Box
                                                            display="flex"
                                                            justifyContent="space-between"
                                                            alignItems="center"
                                                        >
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                            >
                                                                <MedicalServicesIcon
                                                                    sx={{
                                                                        mr: 1,
                                                                    }}
                                                                />
                                                                <Typography
                                                                    variant="h6"
                                                                    fontWeight={
                                                                        700
                                                                    }
                                                                >
                                                                    {
                                                                        workshop
                                                                            .workshopType
                                                                            .label
                                                                    }
                                                                </Typography>
                                                            </Box>
                                                            <Chip
                                                                label={
                                                                    workshop
                                                                        .workshopType
                                                                        .label
                                                                }
                                                                size="small"
                                                                color={
                                                                    workshop
                                                                        .workshopType
                                                                        .chipColor
                                                                }
                                                                sx={{
                                                                    fontWeight: 600,
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>

                                                    <CardContent sx={{ p: 3 }}>
                                                        <Typography
                                                            variant="h6"
                                                            fontWeight={700}
                                                            gutterBottom
                                                            sx={{
                                                                lineHeight: 1.3,
                                                                color: theme
                                                                    .palette
                                                                    .text
                                                                    .primary,
                                                            }}
                                                        >
                                                            {workshop.name}
                                                        </Typography>

                                                        <Divider
                                                            sx={{ my: 2 }}
                                                        />

                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            gap={1}
                                                        >
                                                            <AccessTimeIcon
                                                                fontSize="small"
                                                                color="action"
                                                            />
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                                sx={{
                                                                    lineHeight: 1.6,
                                                                }}
                                                            >
                                                                {workshop.time}
                                                            </Typography>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        );
                                    },
                                )}
                        </Grid>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

export default WorkshopSchedule;
